import React, { useContext, useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useAppDispatch, useAppSelector } from "@/hooks/reduxToolkitHook"
import { transactionsDebitCustomer } from "@/redux/features/transactionsSlice"
import { ActionNotificationContext, ActionNotificationContextValeurType } from "@/context/ActionNotificationContext"
import ModalForm from "components/common/modal/ModalForm"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatMoney } from "@/utils/utils"
import { AlertCircle, Building, Check, ChevronsUpDown, Info, Receipt, Search } from "lucide-react"
import { InvoiceType } from "@/types/InvoicesType"
import { cn } from "@/utils/shadcn-utils"
import { fetchInvoices } from "@/redux/features/invoicesSlice"

/* =======================
   TYPES
======================= */

type TransactionTypeValue =
  | "MANAGER_DEBIT"
  | "CYCLIC_INVOICE_BILL"
  | "REGULARIZATION_INVOICE_BILL"
  | "PARTIAL_CYCLIC_INVOICE_BILL"
  | "PARTIAL_REGULARIZATION_INVOICE_BILL";

interface DebitFormData {
  company_id: string;
  transaction_type: TransactionTypeValue;
  amount: string;
  motif: string;
  invoice_number: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  companyId?: string | null;
}

interface TransactionTypeOption {
  value: TransactionTypeValue;
  label: string;
  description: string;
  invoiceType?: "CYCLIC" | "REGULARIZATION";
  requiresInvoice: boolean;
  isPartial: boolean;
}

/* =======================
   CONSTANTS
======================= */

const TRANSACTION_TYPE_OPTIONS: TransactionTypeOption[] = [
  {
    value: "MANAGER_DEBIT",
    label: "Débit gestionnaire",
    description: "Débit manuel effectué par le gestionnaire",
    requiresInvoice: false,
    isPartial: false,
  },
  {
    value: "CYCLIC_INVOICE_BILL",
    label: "Paiement de facture cyclique",
    description: "Paiement complet d'une facture cyclique",
    invoiceType: "CYCLIC",
    requiresInvoice: true,
    isPartial: false,
  },
  {
    value: "REGULARIZATION_INVOICE_BILL",
    label: "Paiement de facture de régularisation",
    description: "Paiement complet d'une facture de régularisation",
    invoiceType: "REGULARIZATION",
    requiresInvoice: true,
    isPartial: false,
  },
  {
    value: "PARTIAL_CYCLIC_INVOICE_BILL",
    label: "Paiement partiel de facture cyclique",
    description: "Paiement partiel d'une facture cyclique",
    invoiceType: "CYCLIC",
    requiresInvoice: true,
    isPartial: true,
  },
  {
    value: "PARTIAL_REGULARIZATION_INVOICE_BILL",
    label: "Paiement partiel de facture de régularisation",
    description: "Paiement partiel d'une facture de régularisation",
    invoiceType: "REGULARIZATION",
    requiresInvoice: true,
    isPartial: true,
  },
]

/* =======================
   COMPONENT
======================= */

const DebitClientForm = ({ open, onClose, companyId }: Props) => {
  const dispatch = useAppDispatch()
  const { companies } = useAppSelector((state) => state.companies)
  const { invoices, invoicesStatus } = useAppSelector((state) => state.invoices)
  const { displayAlert } = useContext(
    ActionNotificationContext,
  ) as ActionNotificationContextValeurType

  useEffect(() => {
    if (invoicesStatus === "idle") {
      dispatch(fetchInvoices())
    }
  }, [])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<DebitFormData>({
    defaultValues: {
      company_id: companyId || "",
      transaction_type: "MANAGER_DEBIT",
      amount: "",
      motif: "",
      invoice_number: "",
    },
  })

  const selectedCompanyId = watch("company_id")
  const transactionType = watch("transaction_type")
  const selectedInvoiceNumber = watch("invoice_number")

  // Get transaction type config
  const transactionConfig = useMemo(() => {
    return TRANSACTION_TYPE_OPTIONS.find((opt) => opt.value === transactionType)
  }, [transactionType])


  // Get selected company
  const selectedCompany = useMemo(() => {
    return companies.find((c) => c.id === selectedCompanyId)
  }, [companies, selectedCompanyId])

  // Filter invoices by company and invoice type
  const filteredInvoices = useMemo(() => {
    if (!selectedCompanyId || !transactionConfig?.invoiceType) return []

    return invoices.filter((invoice: InvoiceType) => {
      const matchesCompany = invoice.company === selectedCompanyId
      const matchesType = invoice.invoice_type === transactionConfig.invoiceType
      const hasPendingAmount = invoice.total - invoice.paid_amount > 0
      const isPayable = ["PENDING", "PARTIALLY_PAID"].includes(invoice.status)

      return matchesCompany && matchesType && hasPendingAmount && isPayable
    })
  }, [invoices, selectedCompanyId, transactionConfig?.invoiceType])

  // Check if form can be submitted
  const canSubmit = useMemo(() => {
    if (transactionType !== "MANAGER_DEBIT") {
      // Invoice required - check if any available
      if (filteredInvoices.length === 0) return false
    }
    return true
  }, [transactionType, filteredInvoices.length])

  // Get selected invoice
  const selectedInvoice = useMemo(() => {
    return filteredInvoices.find((inv: InvoiceType) => inv!.invoice_number === selectedInvoiceNumber)
  }, [filteredInvoices, selectedInvoiceNumber])

  // Calculate remaining amount for selected invoice
  const invoiceRemainingAmount = useMemo(() => {
    if (!selectedInvoice) return 0
    return selectedInvoice.total - selectedInvoice.paid_amount
  }, [selectedInvoice])

  // Reset invoice and amount when transaction type changes
  useEffect(() => {
    setValue("invoice_number", "")
    setValue("amount", "")
  }, [transactionType, setValue])

  // Reset invoice when company changes
  useEffect(() => {
    setValue("invoice_number", "")
    setValue("amount", "")
  }, [selectedCompanyId, setValue])

  // Auto-fill amount for full invoice payment
  useEffect(() => {
    if (selectedInvoice && transactionConfig && !transactionConfig.isPartial) {
      setValue("amount", invoiceRemainingAmount.toString())
    }
  }, [selectedInvoice, transactionConfig, invoiceRemainingAmount, setValue])

  // Amount validation based on transaction type
  const validateAmount = (value: string) => {
    const numValue = parseFloat(value)

    if (!value || isNaN(numValue)) {
      return "Le montant est requis"
    }

    if (numValue <= 0) {
      return "Le montant doit être supérieur à 0"
    }

    // For full invoice payment, amount must equal remaining
    if (
      transactionConfig &&
      !transactionConfig.isPartial &&
      transactionConfig.requiresInvoice &&
      selectedInvoice
    ) {
      if (numValue !== invoiceRemainingAmount) {
        return `Le montant doit être exactement ${formatMoney(invoiceRemainingAmount)}`
      }
    }

    // For partial payment, amount must not exceed remaining
    if (transactionConfig?.isPartial && selectedInvoice) {
      if (numValue > invoiceRemainingAmount) {
        return `Le montant ne peut pas dépasser ${formatMoney(invoiceRemainingAmount)}`
      }
    }

    return true
  }

  // Form submission
  const onSubmit = async (data: DebitFormData) => {
    if (transactionType !== "MANAGER_DEBIT" && !data.invoice_number) {
      // Display error - invoice is required but none selected or available
      displayAlert("Veuillez sélectionner une facture", "error")
      return
    }

    setIsSubmitting(true)

    const payload = {
      company: data.company_id,
      amount: data.amount,
      transaction_type: data.transaction_type,
      motif: data.motif,
      ...(data.invoice_number && { invoice: data.invoice_number }),
    }

    try {
      await dispatch(transactionsDebitCustomer(payload)).unwrap()
      displayAlert("Transaction créée avec succès", "success")
      handleClose()
    } catch (error) {
      displayAlert(error.data.error ? error.data.error : "La transaction n'a pas pu être créée", "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset({
      company_id: companyId || "",
      transaction_type: "MANAGER_DEBIT",
      amount: "",
      motif: "",
      invoice_number: "",
    })
    onClose()
  }

  return (
    <ModalForm
      open={open}
      onClose={handleClose}
      formSubmit={handleSubmit(onSubmit)}
      title="Débiter le compte du client"
      className="sm:max-w-[760px]"
      submitButtonProps={{
        isLoading: isSubmitting,
        isDisabled: isSubmitting || !canSubmit,
        label: isSubmitting ? "Traitement..." : "Valider",
      }}>
      <div className="space-y-5">
        {/* Company Select - Only if companyId is not provided */}
        {!companyId && (
          <div className="space-y-2">
            <Label>
              Client <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="company_id"
              control={control}
              rules={{ required: "Le client est requis" }}
              render={({ field }) => (
                <Popover open={companyPopoverOpen} onOpenChange={setCompanyPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={companyPopoverOpen}
                      className={cn(
                        "w-full justify-between h-10 font-normal",
                        errors.company_id && "border-red-500",
                      )}>
                      {selectedCompany ? (
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span>{selectedCompany.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          Rechercher un client...
                        </span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start">
                    <Command>
                      <CommandInput placeholder="Rechercher un client..." />
                      <CommandList>
                        <CommandEmpty>
                          <div className="py-6 text-center text-sm">
                            <Search className="mx-auto h-6 w-6 text-muted-foreground mb-2" />
                            <p>Aucun client trouvé</p>
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {companies.map((company) => (
                            <React.Fragment key={company.id}>
                              <CommandItem
                                value={company.name}
                                onSelect={() => {
                                  field.onChange(company.id)
                                  setCompanyPopoverOpen(false)
                                }}
                                className="cursor-pointer">
                                <div className="flex items-center gap-3 w-full">
                                  <Building className="h-4 w-4 text-muted-foreground" />
                                  <span className="flex-1">{company.name}</span>
                                  {field.value === company.id && (
                                    <Check className="h-4 w-4 text-primary" />
                                  )}
                                </div>
                              </CommandItem>
                            </React.Fragment>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            />
            {errors.company_id && (
              <p className="text-sm text-red-500">{errors.company_id.message}</p>
            )}
          </div>
        )}

        {/* Transaction Type */}
        <div className="space-y-2">
          <Label>
            Motif du débit <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="transaction_type"
            control={control}
            rules={{ required: "Le motif est requis" }}
            render={({ field }) => {
              const selectedOption = TRANSACTION_TYPE_OPTIONS.find(
                (opt) => opt.value === field.value,
              )
              return (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className={errors.transaction_type ? "border-red-500" : ""}>
                    <SelectValue placeholder="Sélectionner un motif">
                      {selectedOption?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col items-start">
                          <span>{option.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            }}
          />
          {errors.transaction_type && (
            <p className="text-sm text-red-500">{errors.transaction_type.message}</p>
          )}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label>
            Description ou commentaire <span className="text-red-500">*</span>
          </Label>
          <Textarea
            placeholder="Votre commentaire"
            rows={3}
            {...register("motif", {
              required: "La description est requise",
            })}
            className={errors.motif ? "border-red-500" : ""}
          />
          {errors.motif && (
            <p className="text-sm text-red-500">{errors.motif.message}</p>
          )}
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" />
            Ce commentaire sera visible pour le client
          </p>
        </div>

        {/* Invoice Selection - Only for non-MANAGER_DEBIT transaction types */}
        {
          transactionType !== "MANAGER_DEBIT" && (
            <div className="space-y-3">
              <Label>
                Rattacher à une facture <span className="text-red-500">*</span>
              </Label>

              {!selectedCompanyId ? (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      Veuillez d'abord sélectionner un client pour voir les factures disponibles.
                    </p>
                  </div>
                </div>
              ) : filteredInvoices.length === 0 ? (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-red-800 font-medium">
                        Aucune facture{" "}
                        {transactionConfig?.invoiceType === "CYCLIC"
                          ? "cyclique"
                          : "de régularisation"}{" "}
                        disponible pour ce client.
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Une facture est requise pour ce type de transaction.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <Controller
                  name="invoice_number"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (transactionType !== "MANAGER_DEBIT" && !value) {
                        return "La facture est requise"
                      }
                      return true
                    },
                  }}
                  render={({ field }) => (
                    <div
                      className={cn(
                        "rounded-lg border divide-y max-h-[250px] overflow-y-auto",
                        errors.invoice_number && "border-red-500",
                      )}>
                      {filteredInvoices.map((invoice: InvoiceType) => {
                        const remaining = invoice.total - invoice.paid_amount
                        const isSelected = field.value === invoice.invoice_number

                        return (
                          <div
                            key={invoice.invoice_number}
                            onClick={() => field.onChange(invoice.invoice_number)}
                            className={cn(
                              "flex items-start gap-4 p-4 cursor-pointer transition-colors",
                              isSelected ? "bg-primary/5" : "hover:bg-muted",
                            )}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => field.onChange(invoice.invoice_number)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Receipt className="h-4 w-4 text-muted-foreground" />
                                <p className="text-sm font-medium truncate">
                                  {invoice.invoice_number}
                                </p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Échéance:{" "}
                                {new Date(invoice.due_date).toLocaleDateString("fr-FR")}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-muted-foreground">
                                {transactionConfig?.isPartial
                                  ? "Restant à payer"
                                  : "Montant dû"}
                              </p>
                              <p className="text-sm font-semibold text-red-600">
                                {formatMoney(remaining)}
                              </p>
                              {invoice.paid_amount > 0 && (
                                <p className="text-xs text-green-600">
                                  Déjà payé: {formatMoney(invoice.paid_amount)}
                                </p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                />
              )}
              {errors.invoice_number && (
                <p className="text-sm text-red-500">{errors.invoice_number.message}</p>
              )}
            </div>
          )
        }

        {/* Amount */}
        <div className="space-y-2">
          <Label>
            Montant à débiter (Fcfa) <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            placeholder="100 000"
            {...register("amount", {
              validate: validateAmount,
            })}
            disabled={
              transactionConfig?.requiresInvoice &&
              !transactionConfig?.isPartial &&
              !!selectedInvoice
            }
            className={errors.amount ? "border-red-500" : ""}
          />
          {errors.amount && (
            <p className="text-sm text-red-500">{errors.amount.message}</p>
          )}

          {/* Amount info based on transaction type */}
          {transactionConfig?.requiresInvoice && selectedInvoice && (
            <div
              className={cn(
                "p-3 rounded-lg border text-sm",
                transactionConfig.isPartial
                  ? "bg-blue-50 border-blue-200 text-blue-800"
                  : "bg-green-50 border-green-200 text-green-800",
              )}>
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 flex-shrink-0" />
                {transactionConfig.isPartial ? (
                  <p>
                    Montant maximum autorisé:{" "}
                    <strong>{formatMoney(invoiceRemainingAmount)}</strong>
                  </p>
                ) : (
                  <p>
                    Le montant est automatiquement défini à{" "}
                    <strong>{formatMoney(invoiceRemainingAmount)}</strong> pour un paiement
                    complet.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Info Alert for MANAGER_DEBIT */}
        {transactionType === "MANAGER_DEBIT" && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Pour un débit gestionnaire, aucune facture n'est requise. Le montant sera
                débité directement du portefeuille du client.
              </p>
            </div>
          </div>
        )}
      </div>
    </ModalForm>
  )
}

export default DebitClientForm